import { AxiosError, AxiosResponse } from 'axios';
import { getCaseById, saveCase as saveCaseToApi } from 'modules/cm/api/cases';
import { Action, ACTION_TYPES, CaseErrorDetails, GetState } from './types';
import { CaseDetails, CaseStatus } from 'modules/cm/pages/CaseDetailsPage/CaseDetailsPage.types';
import { Dispatch } from 'redux';
import { caseFromApi, mapCaseToApi, resourcesFromApi } from './mapper';

import { setReadonlyFields } from './readonlyFields/actions';
import * as caseHistoryActions from './caseHistory/actions';
import * as refreshActions from './refresh/actions';

import { toReadonlyFields } from 'modules/cm/mapper/Case/ReadonlyFields/mapper';
import { CaseResponseData } from 'modules/cm/api/cases/types';
import underwritingMapper from 'modules/cm/mapper/Underwriting/mapper';
import { storeUnderwritingSuccess } from 'modules/cm/store/cases/underwriting/actions';
import pepperReviewMapper from 'modules/cm/mapper/PepperReview/mapper';
import * as repaymentStrategyMapper from 'modules/cm/api/cases/repaymentStrategy/mapper';
import { storePepperReviewSuccess } from 'modules/cm/store/cases/pepperReview/actions';
import { setDueDiligenceStatus } from 'modules/cm/store/cases/dueDiligence/actions';
import * as sharedCaseActions from 'modules/cm/store/sharedActions';
import { storeProductsSuccess } from './products/actions';
import { mapProductsToDomain } from 'modules/cm/mapper/Products/mapper';
import { mapRepaymentStrategyToApi } from 'modules/cm/api/cases/repaymentStrategy/mapper/domainToApi';
import { Product } from './products/types';
import { productTypeChecker } from '../../hooks/useProductType';
import { createEmptyRepaymentStrategy } from '../../RepaymentStrategy/RepaymentStrategy';
import { ProductRepaymentTypes } from 'domain/Product';

export const start = (): Action => ({
  type: ACTION_TYPES.PENDING,
  payload: [null, null],
});

export const saveStart = (): Action => ({
  type: ACTION_TYPES.SAVING_START,
  payload: [null, null],
});

export const saveFinished = (): Action => ({
  type: ACTION_TYPES.SAVING_FINISHED,
  payload: [null, null],
});

export const success = (payload: CaseDetails): Action => ({
  type: ACTION_TYPES.SUCCESS,
  payload: [payload, null],
});

export const error = (payload: AxiosError<CaseErrorDetails>): Action => ({
  type: ACTION_TYPES.ERROR,
  payload: [null, payload],
});

export const setErrorModal = (payload: Error): Action => ({
  type: ACTION_TYPES.SET_ERROR_MODAL,
  payload: [payload, null],
});

export const clearErrorModal = (): Action => ({
  type: ACTION_TYPES.SET_ERROR_MODAL,
  payload: [null, null],
});

export const refreshCase = (caseId: string) => {
  return (dispatch: Dispatch<Action>, getState: GetState) => {
    return Promise.all([fetchCase(caseId)(dispatch, getState), dispatch(refreshActions.resetRefresh())]);
  };
};

export const setCaseStatus = (status: CaseStatus) => ({
  type: ACTION_TYPES.SET_CASE_STATUS,
  payload: [status, null],
});

export const fetchCase = (id: string) => {
  return (dispatch: Dispatch<Action>, getState: GetState) => {
    dispatch(start());
    return getCaseById(id)
      .then((res) => {
        try {
          historyPushDispatcher(dispatch, getState)(res);
          const resources = resourcesFromApi(res.data.resources);
          dispatch(storeUnderwritingSuccess(underwritingMapper.mapApiToDomain(res.data.underwriting, resources)));
          dispatch(storePepperReviewSuccess(pepperReviewMapper.mapApiToDomain(res.data.pepperReview, resources)));
          if (res.data.case.repaymentStrategy) {
            sharedCaseActions.setRepaymentData(repaymentStrategyMapper.apiToDomain(res.data.case.repaymentStrategy))(
              dispatch as Dispatch,
            );
          }
          dispatch(storeProductsSuccess(mapProductsToDomain(res.data.products)));
          dispatch(
            success({
              case: caseFromApi(res.data.case),
              resources,
            }),
          );
          dispatch(setDueDiligenceStatus(res.data.dueDiligenceInfo.ddStatus));
          dispatch(setReadonlyFields(toReadonlyFields(res.data.case)));
          sharedCaseActions.setCaseErrors(res.data.error)(dispatch as Dispatch);
        } catch (err) {
          dispatch(setErrorModal(err as Error));
        }
      })
      .catch((err) => {
        dispatch(error(err));
        dispatch(setErrorModal(err));
      });
  };
};

export const saveCase = () => {
  return (dispatch: Dispatch<Action>, getState: GetState) => {
    const state = getState().currentCase;
    let repaymentStrategyState = state.data!.case.repaymentStrategy;
    let repaymentStrategy = null;

    try {
      const products = state.products;
      const formProductCode = state.data!.case.loanDetails.productCode;
      const formRepaymentType = state.data!.case.loanDetails.productRepaymentType;
      const found: Product | undefined = products.find((product: Product) => product.code === formProductCode);
      const isProductType = productTypeChecker(found ? found.type : null);

      if (isProductType('TIO') && formRepaymentType === ProductRepaymentTypes.INTEREST_ONLY) {
        repaymentStrategy = repaymentStrategyState
          ? mapRepaymentStrategyToApi(repaymentStrategyState)
          : mapRepaymentStrategyToApi(createEmptyRepaymentStrategy());
      }

      const mappedData = mapCaseToApi(state.data!.case);

      dispatch(saveStart());
      dispatch(start());
      return saveCaseToApi(mappedData, repaymentStrategy)
        .then((res) => {
          try {
            historyPushDispatcher(dispatch, getState)(res);
            const resources = resourcesFromApi(res.data.resources);
            dispatch(storeUnderwritingSuccess(underwritingMapper.mapApiToDomain(res.data.underwriting, resources)));
            dispatch(storePepperReviewSuccess(pepperReviewMapper.mapApiToDomain(res.data.pepperReview, resources)));
            dispatch(setDueDiligenceStatus(res.data.dueDiligenceInfo.ddStatus));
            dispatch(
              success({
                case: caseFromApi(res.data.case),
                resources,
              }),
            );
            dispatch(setReadonlyFields(toReadonlyFields(res.data.case)));
            sharedCaseActions.setCaseErrors(res.data.error)(dispatch as Dispatch);
            dispatch(saveFinished());
          } catch (err) {
            dispatch(setErrorModal(err as Error));
          }
        })
        .catch((err) => {
          dispatch(saveFinished());
          dispatch(setErrorModal(err));
        });
    } catch (err) {
      dispatch(setErrorModal(err as Error));
    }
  };
};

const historyPushDispatcher = (dispatch: Dispatch<Action>, getState: GetState) => (
  res: AxiosResponse<CaseResponseData>,
) => {
  const resources = resourcesFromApi(res.data.resources);
  const caseDetails: CaseDetails = {
    case: caseFromApi(res.data.case),
    resources,
  };

  return dispatch(caseHistoryActions.push(caseDetails));
};
